import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { Airports, QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { PASSENGER_GROUP_STATUS } from "@/constants/status";
import { decodeArrayBufferToString } from "@/helpers/strings";
import { CommonFilterSwitch } from "@/pages/Plannn/types";
import { CompanionsSchema } from "@/schemas/companions";
import { CreateCustomPassengerPayload, CreateCustomPassengerResponse } from "@/types/custom-passenger";
import { CommonExportReq } from "@/types/export.types";

// -------------- Custom Passengers --------------

export const useCreateCustomPassengerMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<CreateCustomPassengerResponse, Error, CreateCustomPassengerPayload>({
        mutationFn: (data) =>
            axiosInstance
                .post<CreateCustomPassengerResponse>(`/custom-passengers/create`, data)
                .then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_PASSENGERS],
            });
        },
    });
};

export const useDeleteCustomPassengerMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, string>({
        mutationFn: (id) =>
            axiosInstance
                .delete<{ id: string; ok: boolean; message: string }>(`/custom-passengers/delete/${id}`)
                .then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_PASSENGERS],
            });
        },
    });
};

export const useNewSaveDataGridChanges = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<
        { id: string; ok: boolean; message: string }[],
        Error,
        {
            changes: {
                id: string;
                companions?: CompanionsSchema;
                showToWelcomeTeam?: CommonFilterSwitch;
                airport?: string;
                location?: string;
                flightNumber?: string; // Only for self arrivals
                pickUp?: DateTime;
                dropOff?: DateTime;
                driver?: string | null;
            }[];
        }
    >({
        mutationFn: ({ changes }) =>
            axiosInstance.post(`/tasks/new-save-data-grid-changes`, { changes }).then((res) => res.data),
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_PASSENGERS],
            });
            return res;
        },
    });
};

export const useChangePassengerStatusMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<
        { id: string; ok: boolean; message: string }[],
        Error,
        {
            passengerIds: string[];
            status: keyof typeof PASSENGER_GROUP_STATUS;
        }
    >({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance
                    .put(`/passengers/passenger-data/status`, payload)
                    .then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
    });
};

interface ExportNewPlanParams extends CommonExportReq {
    date: DateTime;
    cancelled: CommonFilterSwitch;
    arrival: CommonFilterSwitch;
    airport: Airports | null;
    hideSelfTransport: boolean;
    selectedDrivers: string[];
}
export const useExportNewPlanList = () => {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ArrayBuffer, Error, ExportNewPlanParams>({
        mutationFn: ({ date, ...payload }) => {
            return axiosInstance
                .post<ArrayBuffer>(`/passengers/plan/${date.toISODate()}/export`, payload, {
                    responseType: "arraybuffer",
                })
                .then((res) => res.data)
                .catch((err) => {
                    Sentry.captureException(err);
                    const errorBuffer = (err as AxiosError<ArrayBuffer>)?.response?.data as ArrayBuffer;
                    if (errorBuffer) {
                        const errorMessage = decodeArrayBufferToString(errorBuffer);
                        enqueueSnackbar(errorMessage, {
                            variant: "error",
                        });
                    }
                    throw err;
                });
        },
    });
};
